import {
  JobsiteFiltersOptions,
  JobsitesFilters,
} from 'containers/jobsites/jobsitesWidget/types';
import React from 'react';
import { FilterItems, SelectOptionElement } from '@odin-labs/components';
import {
  Contractor,
  ContractorWorker,
  JobsiteContractor,
  JobsiteWorker,
  User,
  UserIdentity,
  Worker,
  useGetJobsiteContractorWorkersQuery,
  useGetJobsiteContractorsQuery,
  JobsitePrinter,
  useGetJobsiteWithPrintersQuery,
} from 'apollo/generated/client-operations';
import { getOrganizationName, getUserFullName } from 'utils';

export const validateScannedBadgeQrCode = (value: string): string => {
  if (value?.startsWith('http') && value?.includes('worker')) {
    throw new Error(
      'This looks like a worker QR code. Please scan the QR code on the back of the badge instead.',
    );
  }
  return value;
};

export type JobsiteContractorForOption = Pick<JobsiteContractor, 'id'> & {
  contractor: Pick<Contractor, 'contractorId' | 'isDefaultContractor'> & {
    organization: Pick<
      Contractor['organization'],
      'organizationId' | 'name' | 'legalName'
    >;
  };
};

export type JobsiteContractorOption = SelectOptionElement & {
  organizationId: string;
};

export const getJobsiteContractorsOptions = (
  jobsiteContractors: JobsiteContractorForOption[],
): JobsiteContractorOption[] =>
  jobsiteContractors
    ?.map((jc) => {
      const { id: value, contractor } = jc;
      const { organization, isDefaultContractor } = contractor;
      const { organizationId } = organization;
      return {
        label: getOrganizationName(organization),
        value,
        isDefaultContractor,
        organizationId,
      };
    })
    .filter(({ isDefaultContractor }) => !isDefaultContractor)
    .sort((a, b) => a.label.localeCompare(b.label));

export const getJobsiteFilterItems = ({
  filtersOptions,
  developerIds,
  cities,
  states,
  search,
}: {
  filtersOptions: JobsiteFiltersOptions;
  developerIds: string[];
  cities: string[];
  states: string[];
  search: string;
}): FilterItems<JobsitesFilters> => ({
  developerIds: {
    header: 'Client',
    type: 'dropdown',
    defaultValue: developerIds,
    componentProps: {
      options: filtersOptions?.developers ?? [],
      allowSearch: false,
    },
  },
  cities: {
    header: 'City',
    type: 'dropdown',
    defaultValue: cities,
    componentProps: {
      options: filtersOptions?.cities ?? [],
      isMulti: true,
      allowSearch: false,
    },
  },
  states: {
    header: 'State',
    type: 'dropdown',
    defaultValue: states,
    componentProps: {
      options: filtersOptions?.states ?? [],
      isMulti: true,
      allowSearch: false,
    },
  },
  search: {
    header: '',
    type: 'searchInput',
    defaultValue: search,
    isVisibleOutsideFilterOnMobile: true,
  },
});

export const useJobsiteContractorsOptions = (args: {
  jobsiteId: string;
  skip?: boolean;
}): JobsiteContractorOption[] => {
  const { jobsiteId, skip } = args;
  const { data } = useGetJobsiteContractorsQuery({
    fetchPolicy: 'no-cache',
    skip: !!skip || !jobsiteId,
    variables: { jobsiteId },
  });

  return React.useMemo(() => {
    const jobsiteContractors = data?.getJobsite.jobsiteContractors.edges.map(
      ({ node }) => node,
    );
    return getJobsiteContractorsOptions(jobsiteContractors) ?? [];
  }, [data]);
};

export type JobsiteWorkerForOption = Pick<
  JobsiteWorker,
  'jobsiteWorkerId' | 'trade' | 'jobTitle' | 'isBanned' | 'banExpiresAt'
> & {
  contractorWorker: Pick<ContractorWorker, 'id'> & {
    worker: Pick<Worker, 'workerId' | 'quickCode'> & {
      user: Pick<User, 'userAccountId'> & {
        identity?: Pick<UserIdentity, 'firstName' | 'lastName'>;
      };
    };
  };
};

export type JobsiteWorkerOptionElement = SelectOptionElement & {
  trade?: string;
};

export const getJobsiteWorkersOptions = (
  jobsiteWorkers: JobsiteWorkerForOption[],
): JobsiteWorkerOptionElement[] =>
  jobsiteWorkers
    ?.map((jw) => ({
      value: jw.jobsiteWorkerId,
      label: getUserFullName(jw.contractorWorker.worker.user),
      trade: jw.trade,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const useJobsiteContractorWorkersOptions = (args: {
  jobsiteContractorId: string;
  skip?: boolean;
}): SelectOptionElement[] => {
  const { jobsiteContractorId, skip } = args;
  const { data } = useGetJobsiteContractorWorkersQuery({
    fetchPolicy: 'no-cache',
    skip: !!skip || !jobsiteContractorId,
    variables: { jobsiteContractorId },
  });

  return React.useMemo(() => {
    const jobsiteWorkers = data?.getJobsiteContractor.jobsiteWorkers.edges.map(
      ({ node }) => node,
    );
    return getJobsiteWorkersOptions(jobsiteWorkers) ?? [];
  }, [data]);
};

export type WorkerOptionElement = SelectOptionElement & {
  trade: string;
  title: string;
  quickCode: string;
  isBanned?: boolean;
  banExpiresAt?: string;
};

export const getWorkersOptions = (
  jobsiteWorkers: JobsiteWorkerForOption[],
): WorkerOptionElement[] => {
  return jobsiteWorkers
    ?.map((jw) => {
      const { workerId, user, quickCode } = jw.contractorWorker.worker;
      return {
        value: workerId,
        label: getUserFullName(user),
        trade: jw.trade,
        title: jw.jobTitle,
        quickCode,
        isBanned: jw.isBanned,
        banExpiresAt: jw.banExpiresAt,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export type JobsitePrinterOption = SelectOptionElement &
  Pick<
    JobsitePrinter,
    | 'hidPrinterId'
    | 'fargoLocationId'
    | 'fargoOrganizationId'
    | 'name'
    | 'locationName'
    | 'organizationName'
  >;

export const useJobsitePrinterOptions = (
  jobsiteId: string,
): {
  jobsitePrinterOptions: JobsitePrinterOption[];
  defaultPrinterOption: JobsitePrinterOption | null;
  loading: boolean;
} => {
  const { data, loading } = useGetJobsiteWithPrintersQuery({
    fetchPolicy: 'no-cache',
    skip: !jobsiteId,
    variables: { jobsiteId },
  });

  return React.useMemo(() => {
    if (!data)
      return {
        jobsitePrinterOptions: [],
        defaultPrinterOption: null,
        loading,
      };
    const jobsitePrinters = data?.getJobsite.jobsitePrinters.edges.map(
      ({ node }) => node,
    );
    const defaultJobsitePrinterId = data?.getJobsite.defaultJobsitePrinterId;
    const jobsitePrinterOptions = jobsitePrinters?.map((jp) => {
      return {
        value: jp.id,
        label: `${jp.name ?? jp.hidPrinterId} [${
          jp.locationName ?? jp.fargoLocationId
        }]`,
        ...jp,
      };
    });
    return {
      loading,
      jobsitePrinterOptions,
      defaultPrinterOption: defaultJobsitePrinterId
        ? jobsitePrinterOptions?.find(
            (opt) => opt.id === defaultJobsitePrinterId,
          ) ?? null
        : null,
    };
  }, [data, loading]);
};
